<template>
  <div>
    <ViewHeader>
      <CButton
        color="secondary"
        class="ra-button"
        :class="{active: filters?.assignee_str?.value !== ''}"
        @click="setEmailFilter"
        :pressed="filters?.assignee_str?.value !== ''"
      >
        My assignation
      </CButton>
      <CButton
        color="secondary"
        class="ra-button"
        @click="exportTickets"
      >
        Export tickets
      </CButton>
      <CButton
        color="primary"
        class="ra-button"
        data-cy="create-ticket-link"
        to="/list-of-tasks/create"
      >
        Create Ticket
      </CButton>
    </ViewHeader>

    <div
      class="
        d-flex
        align-items-center
        justify-content-between
        mt-2
        ticket-list-header
      "
    >
      <MzTabs v-model="currentTab" :tabs="tabs" :key="refreshTabs">
        <template #badge="tab">
          <div
            class="badge badge-primary d-flex justify-content-center"
            data-cy="ticket-count-badge"
            :data-cy-value="0 <= +ticketsMeta[tab.name]"
          >
            {{ ticketsMeta[tab.name] || 0 }}
          </div>
        </template>
      </MzTabs>
      <div>
        <div class="d-flex align-items-center justify-content-end">
          <CButton
            v-if="showResetButton"
            color="link"
            class="ra-button"
            @click="resetFilters"
            data-cy="reco-reset-filters-btn"
          >
            Reset filters
          </CButton>
        </div>
      </div>
    </div>
    <div
      v-if="ticketsMeta === 0"
      class="d-flex align-items-center justify-content-center"
    >
      <div class="d-flex flex-column align-items-center justify-content-center">
        <img src="/img/undraw/no_tickets.svg" class="w-100" />
      </div>
    </div>
    <tableComponent
      :table-date-format="tableDateFormat"
      class="ticket-table"
      :url="TICKETS_ENDPOINTS.main"
      ref="ticketTable"
      :columns="columnsToDisplay"
      :filter-exclude-columns="['actions']"
      :sort-exclude-columns="['actions']"
      :onResult="(results, query, _) => handleTableResult(results, query)"
      :key="`${currentTab}_${refreshTable}
            _${availableEntities.value.length}
            _${availableEntitiesGroup.value.length}_ticket_table`"
      :init-filters="initFilters"
      :force-filter="true"
      :table-override-filters="tableOverrideFilters"
      :headings="headings"
      disableSearchParams
    >
      <template #filter__entity_code>
        <InputComponent
          class="entity-code-filter"
          :key="availableEntities.value.join(',')"
          :formField="availableEntities"
          append-dropdown-to-body
        />
      </template>
      <template #filter__entity_group>
        <InputComponent
          class="entity-code-filter"
          :key="availableEntitiesGroup.value.join(',')"
          :formField="availableEntitiesGroup"
          append-dropdown-to-body
        />
      </template>
      <template #title="{ row }">
        <CLink
          :to="`/list-of-tasks/${row.ticket_number}`"
          :data-cy="`ticket_${row.title}`"
        >
          {{ row.title }}
        </CLink>
      </template>
      <template #due_date="{ row }">
        <div class="due-date">
          <span :class="dateColor(row)">
            {{ moment(row.due_date).format(tableDateFormat) }}
          </span>
        </div>
      </template>
      <template #entity_group="{ row }">
        <div class="entity_group">
          <span>
            {{ formatGroup(row.entity_details.group) }}
          </span>
        </div>
      </template>
      <template #entity_name="{ row }">
        <div class="entity_name">
          <span>
            {{ row.entity_details.name }}
          </span>
        </div>
      </template>
      <template #amount="{ row }">
        <div v-if="row.amount">
          {{ row.amount.toLocaleString().replace(",", ".") }}
        </div>
        <div v-else>-</div>
      </template>
      <template #ticket_number="{ row }">
        {{ $getNumericalValue(row.ticket_number, 0, 0) }}
      </template>
      <template #label="{ row }">
        <CBadge
          color="primary"
          v-for="label in row.label"
          :key="label"
          class="text-white h6 mb-0"
        >
          {{ label }}
        </CBadge>
      </template>
      <template #attached_file="{ row }">
        <div v-if="row.attached_file" class="w-100 text-center">
          <i
            class="mz-icon icon-attache font-2xl"
            v-c-tooltip="{
              content: `FILES: ${extractFileNames(row)}`,
              placement: 'top',
            }"
          />
        </div>
        <div v-else></div>
      </template>
      <template #actions="{ row }">
        <CButton
          v-c-tooltip="'Duplicate ticket'"
          class="ra-icon-button h5 mb-0"
          :to="`/list-of-tasks/${row.ticket_number}?duplicate=1`"
        >
          <i class="far fa-copy" />
        </CButton>
      </template>
    </tableComponent>
  </div>
</template>

<script>
import moment from "moment";
import { request } from "@/utils/request";
import { API_ENDPOINTS } from "@/constants/common";
import { TICKETS_ENDPOINTS, TicketsService } from "@/services/tickets";
import { EntitiesService } from "@/services/entity";
import InputComponent from "@/components/InputComponent";
import { UsersMixin } from "@/mixins/users";
import { ActiveUser } from "@/services/user";

export default {
  name: "TicketsList",
  components: {
    InputComponent,
  },
  mixins: [UsersMixin],
  data() {
    return {
      moment,
      API_ENDPOINTS,
      TICKETS_ENDPOINTS,
      tableDateFormat: "DD-MM-YYYY",
      refreshTable: 0,
      showResetButton: false,
      currentTab: null,
      filters: {},
      tabs: ["Open", "Closed", "All"],
      refreshTabs: 0,
      ticketsMeta: {},
      availableEntities: {
        hideLabel: true,
        name: "entity_code",
        placeholder: "Entity Code",
        type: "multiselect",
        value: [],
        options: [],
      },
      availableEntitiesGroup: {
        hideLabel: true,
        name: "entity_group",
        placeholder: "search DF",
        type: "multiselect",
        value: [],
        options: [],
      },
      tableOverrideFilters: {
        attached_file: {
          options: [
            { value: "null", label: "All" },
            { value: "true", label: "Attached files" },
            { value: "false", label: "No files" },
          ],
        },
      },
      columns: [
        "ticket_status",
        "ticket_number",
        "entity_group",
        "entity_code",
        "entity_name",
        "title",
        "amount",
        "label_str",
        "contexte",
        "services_str",
        "created_by",
        "assignee_str",
        "attached_file",
        "created_at",
        "due_date",
        "actions",
      ],
      tickets: [],
      ticketData: [],
      copyEntities: {},
      listEntities: [],
    };
  },
  async mounted() {
    this.createFiltersObject();
    this.initializeFilters();
    await this.populateEntities();
    await this.populateMembers();
    this.currentTab = this.$route.query?.tab || "Open";
    await this.fetchTicketsMeta();
    await this.fetchTickets()
  },
  watch: {
    filters: {
      deep: true,
      handler: function() {
        this.fetchTicketsMeta()
      }
    }
  },
  computed: {
    initFilters() {
      let tableFilters
      if (Object.keys(this.tableEntityFilters).length !== 0){
        this.tableDfFilters = {}
        tableFilters = {
          ...this.tableEntityFilters,
          ...this.tablesFilters,
          search_ticket_status: this.currentTab,
        };
      } else{
        tableFilters = {
          ...this.tableEntityFilters,
          ...this.tablesFilters,
          ...this.tableDfFilters,
          search_ticket_status: this.currentTab,
        };
      }
      if (this.currentTab === "All") {
        delete tableFilters["search_ticket_status"];
      }
      if (this.$getSearchParam("search_label"))
        tableFilters["search_label"] = this.$getSearchParam("search_label");
      return tableFilters;
    },
    columnsToDisplay() {
      return this.currentTab === "All"
        ? this.columns
        : this.columns.filter((c) => c !== "ticket_status");
    },
    headings() {
      const headingsObject = {};
      this.columns.forEach(
        (column) =>
          (headingsObject[column] = column
            .replace("_str", "")
            .replaceAll("_", " "))
      );
      headingsObject["created_by"] = "Author";
      headingsObject["contexte"] = "Context";
      headingsObject["entity_group"] = "DF";
      return headingsObject;
    },
    tableEntityFilters() {
      if (!this.availableEntities.value.length) return {};
      return { search_entity_code: this.availableEntities.value };
    },
    tableDfFilters(){
      this.availableEntities.value = []
      return { search_entity_group: this.availableEntitiesGroup.value };
    },
    tablesFilters() {
      const filters = {};
      Object.keys(this.filters).forEach((filter) => {
        if (this.filters[filter].value) {
          filters[`search_${filter}`] = this.filters[filter].value;
        }
      });
      return filters;
    },
  },
  methods: {
    formatGroup(group) {
      return Array.isArray(group) ? group.join(",") : group;
    },
    exportTickets() {
      const ticketsTableQuery = this.$refs.ticketTable.tableQuery;
      this.$downloadFile({
        url: TICKETS_ENDPOINTS.exportTickets,
        query: {
          ...ticketsTableQuery,
          search_ticket_status: this.currentTab === 'All' ? '' : this.currentTab,
        },
      })
    },
    createFiltersObject() {
      this.columns.forEach((columnName) => {
        this.filters[columnName] = {
          hideLabel: true,
          name: columnName,
          value: "",
          placeholder: `Filter by ${this.headings[columnName]}`,
        };
      });
    },
    initializeFilters() {
      const previousFilters = TicketsService.readListFilters();
      Object.entries(previousFilters).forEach(([key, value]) => {
        key = key.replace("search_", "");
        if (this.filters[key]) {
          this.filters[key].value = value || "";
        }
      });
      if (previousFilters.search_entity_code) {
        this.availableEntities.value = previousFilters.search_entity_code;
      }
    },
    dateColor(row) {
      if (row.ticket_status.toLowerCase() === "open") {
        const newDate = new Date();
        newDate.setHours(0, 0, 0, 0);
        const ticket_date = new Date(row.due_date);
        const ticket_date_ago = new Date(row.due_date);
        ticket_date_ago.setDate(ticket_date_ago.getDate() - 3);
        if (newDate > ticket_date) {
          return "danger";
        } else if (newDate > ticket_date_ago && newDate <= ticket_date) {
          return "warning";
        }
      }
    },
    extractFileNames(row) {
      return [
        ...new Set([
          ...this.extractNamesFromComments(row.comments),
          ...this.extractNamesFromDescription(row.description),
        ]),
      ];
    },
    extractNamesFromDescription(description) {
      const regExp = /\[(.*?)\]/;
      const fileNames = [];
      if (description && description.includes("file_id")) {
        const parts = description
          .split("/")
          .filter((el) => el.includes("file_id"));
        parts.forEach((part) => {
          const match = regExp.exec(part);
          if (match) {
            fileNames.push(match[1]);
          }
        });
      }
      return fileNames;
    },
    extractNamesFromComments(comments) {
      let fileNames = [];
      if (Array.isArray(comments)) {
        comments.forEach((obj) => {
          if (obj.comments && String(obj.comments).includes("file_id")) {
            fileNames = [
              ...fileNames,
              ...this.extractNamesFromDescription(obj.comments),
            ];
          } else if (
            obj.new_value &&
            String(obj.new_value).includes("file_id")
          ) {
            fileNames = [
              ...fileNames,
              ...this.extractNamesFromDescription(obj.new_value),
            ];
          } else if (
            obj.old_value &&
            String(obj.old_value).includes("file_id")
          ) {
            fileNames = [
              ...fileNames,
              ...this.extractNamesFromDescription(obj.old_value),
            ];
          }
        });
      }
      return fileNames;
    },
    async fetchTickets(){
      this.ticketData = await TicketsService.getTickets()
    },
    async fetchTicketsMeta() {
      const query = Object.fromEntries(
        Object.entries(this.initFilters).map(([key, value]) => [
          key,
          Array.isArray(value) ? value.join("|") : value,
        ])
      );
      const { data } = await request({
        url: TICKETS_ENDPOINTS.meta,
        query,
      });
      this.ticketsMeta = data;
    },
    resetFilters() {
      TicketsService.clearListFilters();
      this.createFiltersObject();
      this.initializeFilters();
      this.showResetButton = false;
      this.availableEntities.value = [];
      this.availableEntitiesGroup.value = [];
      this.refreshTable += 1;
      this.availableEntities.options = this.copyEntities
    },
    setEmailFilter() {
      console.log({
        val: this.filters?.assignee_str?.value,
      })
      if (this.filters.assignee_str.value) this.resetFilters();
      else {
        this.filters.assignee_str.value = ActiveUser.get().email;
      }
    },
    async populateEntities() {
      const entities = await EntitiesService.getAllEntities();
      const entitiesList = entities.map((entity) => {
        return {
          value: entity.code,
          name: entity.code,
          group: entity.group,
        };
      });
      this.listEntities = entitiesList
      let entitiesOnTop = [
        "ALL",
        "DFF",
        "OFFSHORE",
        "CORPORATE",
        "LUXEL",
        "EGM",
      ];
      const restEntities = entitiesList.filter(
        (entity) => !entitiesOnTop.includes(entity.value)
      );
      entitiesOnTop = entitiesOnTop
        .map((e) => {
          const entityIndex = entitiesList.findIndex(
            (entity) => entity.value === e
          );
          if (entityIndex !== -1) return entitiesList[entityIndex];
        })
        .filter((e) => e);
      this.availableEntities.options = [
        ...entitiesOnTop,
        ...restEntities.sort((a, b) => {
          return a.value.localeCompare(b.value);
        }),
      ];
      this.copyEntities = this.availableEntities.options
      this.availableEntitiesGroup.options = entitiesOnTop
    },
    persistTableFilters(query) {
      const filtersToPersist = {};
      Object.keys(this.filters).forEach((key) => {
        this.filters[key].value = "";
      });
      Object.entries(query).forEach(([key, value]) => {
        if (key.startsWith("search_")) {
          const filterName = key.replace("search_", "");
          if (this.filters[filterName]) {
            this.filters[filterName].value = value;
          }
          filtersToPersist[key] = value;
        }
      });
      if (filtersToPersist["search_ticket_status"])
        delete filtersToPersist["search_ticket_status"];
      TicketsService.storeListFilters(filtersToPersist);
    },
    handleTableResult(results, query) {
      query["search_entity_code"] = this.availableEntities.value;
      this.persistTableFilters(query);
      this.showResetButton = Object.entries(query).some(
        ([searchParam, value]) =>
          searchParam.includes("search_") &&
          value &&
          value !== "" &&
          value?.length
      );
    },
  },
};
</script>
<style lang="scss">
.ticket-table .multiselect__tags-wrap {
  flex-wrap: nowrap;
}

.ra-button.active {
  background-color: #0071ce;
  color: white;
}

.ticket-table {
  .VueTables__title-filter-wrapper {
    .input-group-wrapper {
      width: 8vw;
    }
  }

  .VueTables__table thead tr {
    th:first-child {
      padding-left: 12px !important;
    }

    th {
      min-width: 130px !important;
    }

    th:nth-child(5),
    th:nth-child(8),
    th:nth-child(9) {
      min-width: 200px !important;
    }

    th:last-child {
      padding-right: 12px !important;
    }
  }

  .due-date {
    .danger {
      font-size: 15px;
      font-weight: bold;
      color: rgb(190, 7, 18);
    }

    .warning {
      color: rgb(251, 88, 36);
    }
  }

  .VueTables {
    max-height: calc(100vh - 337px) !important;
  }
}

.custom-filter {
  position: absolute;
  display: flex;
  background-color: white;
  justify-content: left;
}

.entity-code-filter {
  .multiselect__content-wrapper {
    max-height: 150px !important;
  }
}

th {
  text-transform: capitalize;
}

.mz-select__container {
  box-shadow: none;
}

.mz-select__container .mz-options.options-opened {
  width: 300px !important;
}

.mz-select-after-list {
  width: 300px;
  border-bottom: 1px solid #e9e9e9;
}
</style>
